import { autoinject, transient, observable, computedFrom, BindingEngine } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue, LookupValueResponse } from "../../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import * as Enumerable from "linq";
import { Router } from "aurelia-router";
import { BaseActivity } from "../base-activity";
import { TasksConstants } from "../../../../../models/tasks/task.model";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../../utilities/guid";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { GhgVerificationStatusEnum, GhgVerificationActivitiesColumns } from "../../../../../models/ghgs/ghg.models";
import { CustomerService } from "../../../../../services/modules/customer-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { GetBusinessUnitIdsResponse } from "../../../../../models/customers/customer.models";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';
import { Reflection } from "@rightship-tech/common-ui/helpers/reflection";

@autoinject()
@transient()
export class GhgVerificationActivities extends BaseActivity {

    private _isReloading: boolean;

    public customerEntityTypes: EntityTypeConfigurationModel[];
    public companiesEntityTypes: EntityTypeConfigurationModel[];
    @cachable
    selectedStatusFilter: number[] = [];
    @cachable
    mobileSelectedFilter: number[] = [];
    ghgStatuses: LookupValue[];
    ghgOutcomes: LookupValue[];
    taskTypes: { id: number, name: string, propertyName: string }[] = [{
        id: 3,
        name: "EEXI",
        propertyName: 'verifyEexi'
    }, {
        id: 1,
        name: 'GHG Rating',
        propertyName: 'verifyGhg'
    }, {
        id: 2,
        name: "Energy Saving Equipment",
        propertyName: 'verifyEnergySavingEquipment'
    }, {
        id: 4,
        name: "CII",
        propertyName: 'verifyCii'
    }];
    isMobileFilterOpen: boolean;
    @observable()
    @cachable
    filter: string;
    @cachable
    appliedFilter: string;
    public defaultPageCount: number = 100;
    @cachable
    public myGhgsFilterSelected: boolean = false;

    public newGhgCount: number;
    public inProgressCount: number;
    public awaitingResponseCount: number;
    public pendingShipManagerCount: number;
    public reviewRequiredCount: number;
    public myGhgCount: number;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    @cachable
    public rightshipMember: boolean = false;
    public accessableBusinessUnits: LookupValue[] = [];
    public isColumnFiltering: boolean = false

    public isCustomer: boolean = false;
    public pendingVetCount: number = 0;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private lookupClientService: LookupClientService, private signalClientService: SignalClientService, router: Router,
        private userClientService: UserClientService, private userClaimsService: UserClaimsService, private queryService: QueriesClientService,
        private customerService: CustomerService,
        private commonCustomerService: CommonCustomerService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        super(i18N, router, TasksConstants.GHGListName, bindingEngine);
        this.customerEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Customers"]);
        this.companiesEntityTypes = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 3 : 2,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: this.rightShipUser ? null : false,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            },
            {
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusRank',
                title: 'StatusRank'
            },
            {
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 2 : 3,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusDate',
                title: 'StatusDate'
            });
        return columns;
    }

    protected setDefaultFilters() {
        this.mobileFilterApply(false);
    }

    async init() {
        const promises = [];
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAccessDueDiligence = this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence);

        if (!this.rightShipUser) {
            promises.push(this.customerService.getAllAccessableBusinessUnitsByType(EntityTypes.GHGVerification));

            const request = new LookupValueRequest();
            request.lookupValueQuery = LookupValueQuery.BusinessUnits;

            promises.push(this.lookupClientService.getLookup(request));
        }

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.GhgVerificationStatuses;
        promises.push(this.lookupClientService.getLookup(request));

        const handleStatusLookupResult = (x: LookupValueResponse) => {

            const values = x.activeValues;

            this.ghgStatuses = Enumerable.from(values.filter(x => x.isActive)).orderBy(x => x.additionalData.statusRank)
                .where(x =>
                    x.id != GhgVerificationStatusEnum.GhgVerificationReassignment
                    && x.id != GhgVerificationStatusEnum.Accepted
                    && x.id != GhgVerificationStatusEnum.Rejected
                    && x.id != GhgVerificationStatusEnum.Cancelled
                    && x.id != GhgVerificationStatusEnum.Expired).toArray();
            this.ghgOutcomes = Enumerable.from(values).orderBy(x => x.id)
                .where(x =>
                    x.id == GhgVerificationStatusEnum.Accepted
                    || x.id == GhgVerificationStatusEnum.Rejected
                    || x.id == GhgVerificationStatusEnum.Cancelled).toArray();
        };

        const handleBULookupResult = (bulookupResult: LookupValueResponse, accessibleBUs: GetBusinessUnitIdsResponse) => {
            this.accessableBusinessUnits = bulookupResult.activeValues.filter(value => accessibleBUs.businessUnitIds.indexOf(value.id) > -1);
        }

        const results = await Promise.all(promises);

        if (!this.rightShipUser) {
            handleBULookupResult(results[1], results[0]);
            handleStatusLookupResult(results[2]);
        }
        else {
            handleStatusLookupResult(results[0]);
        }

        this.columns = GhgVerificationActivitiesColumns.getColumns(this.rightShipUser);

        this.initialSorting = this.getSorting();

        const customerId = this.userClientService.currentUser.settings.customerId;
        //fallback get customerid
        if (customerId == null || customerId <= 0) {
            try {
                const customerIdResponse = await this.commonCustomerService.getCustomerId();
                this.isCustomer = (customerIdResponse && customerIdResponse > 0);
            }
            catch (e) {
                console.warn(e);
            }
        }
        else this.isCustomer = true;

        this.applyCache();
        this.setItemSource();
    }

    async activate() {
        await this.init();
        this.refreshActivityCounts();
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/GhgVerifications",
            columns: this.columns,
            initialFilters: filters,
            initialSorting: this.initialSorting
        };
    }

    async refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessEnvironmentals)) {
            const x = await this.queryService.getData(`odata/GhgVerifications/Genesis.GetStatusCounts()`);
            this.clearCounts();

            for (let i = 0; i < x.keys.length; i++) {

                const key = x.keys[i];
                const value = x.values[i];

                if (key == GhgVerificationStatusEnum.Pending) {
                    this.newGhgCount = value;
                } else if (key == GhgVerificationStatusEnum.InProgress) {
                    this.inProgressCount = value;
                } else if (key == GhgVerificationStatusEnum.AwaitingResponse) {
                    this.awaitingResponseCount = value;
                } else if (key == GhgVerificationStatusEnum.PendingShipManager) {
                    this.pendingShipManagerCount = value;
                } else if (key == GhgVerificationStatusEnum.PendingReview) {
                    this.reviewRequiredCount = value;
                } else if (key == -1) {
                    this.myGhgCount = value;
                } else if (key == -2) {
                    this.pendingVetCount = value;
                }
                else if (key == -3) {
                    this.pendingDueDiligenceCount = value;
                }
            }
        }
    }

    clearCounts() {
        this.newGhgCount = 0;
        this.inProgressCount = 0;
        this.awaitingResponseCount = 0;
        this.pendingShipManagerCount = 0;
        this.reviewRequiredCount = 0;
        this.myGhgCount = 0;
        this.pendingDueDiligenceCount = 0;
    }

    getCount(statusId: number) {
        if (statusId == GhgVerificationStatusEnum.Pending && this.newGhgCount)
            return this.newGhgCount;
        if (statusId == GhgVerificationStatusEnum.InProgress && this.inProgressCount)
            return this.inProgressCount;
        if (statusId == GhgVerificationStatusEnum.AwaitingResponse && this.awaitingResponseCount)
            return this.awaitingResponseCount;
        if (statusId == GhgVerificationStatusEnum.PendingShipManager && this.pendingShipManagerCount)
            return this.pendingShipManagerCount;
        if (statusId == GhgVerificationStatusEnum.PendingReview && this.reviewRequiredCount)
            return this.reviewRequiredCount;

        return 0;
    }

    rightshipMemberClick(isMobile: boolean = false) {
        this.rightshipMember = !this.rightshipMember;
        if (!isMobile) this.setItemSource();
    }

    filterClick(ghgStatus: LookupValue) {
        const index = this.selectedStatusFilter.indexOf(ghgStatus.id);
        const isSelected = index != -1;
        if (!isSelected) {
            this.selectedStatusFilter.push(ghgStatus.id);
        } else {
            this.selectedStatusFilter.splice(index, 1);
        }

        this.setItemSource();
    }

    myGhgsClick(isMobile: boolean = false) {
        this.myGhgsFilterSelected = !this.myGhgsFilterSelected;
        if (!isMobile) this.setItemSource();
    }

    @cachable
    public isPendingVet: boolean = false;

    pendingVetClick(isMobile: boolean = false) {
        this.isPendingVet = !this.isPendingVet;
        if (!isMobile) this.setItemSource();
    }

    private setItemSource() {
        const currentFilters: QueryFilter[] = [];

        if (this.selectedStatusFilter && this.selectedStatusFilter.length > 0) {
            this.selectedStatusFilter.forEach(x => {
                currentFilters.push(QueryFilter.getQuery("statusId", x, "eq", DataType.Number, false, false, "statusGroup", true));
            });
        }

        if (this.myGhgsFilterSelected) {
            currentFilters.push(QueryFilter.getQuery("reviewerId", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery("statusId", [
                GhgVerificationStatusEnum.Pending,
                GhgVerificationStatusEnum.InProgress,
                GhgVerificationStatusEnum.AwaitingResponse,
                GhgVerificationStatusEnum.PendingReview,
                GhgVerificationStatusEnum.GhgVerificationReassignment,
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.isPendingVet) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("hasVet", this.isPendingVet, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.isPendingDueDiligence && this.dueDiligenceFilterVisible) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("hasDueDiligence", this.isPendingDueDiligence, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.buFilterActive && !this.rightShipUser) {

            const buFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "businessUnitId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedBUs || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(buFilter);
        }

        if (this.customerFilterActive && this.rightShipUser) {

            const customerFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "customerId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCustomers || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(customerFilter);
        }

        if (this.ghgOutcomeFilterActive) {

            const ghgOutcomeFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "statusId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedGhgOutcomes || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(ghgOutcomeFilter);
        }

        if (this.taskTypeFilterActive) {

            const taskTypeFilters = this.getTaskTypeFilters();

            currentFilters.push(...taskTypeFilters);
        }

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("ghgVerificationIdStr", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        if (this.rightshipMember) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
        }
        
        //#region doc company
        if (this.docCompanyFilterActive) {

            const docCompanyFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "docCompanyId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedDocCompanies || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(docCompanyFilter);
        }
        //#endregion

        //#region dwt
        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        this.itemSource = this.getItemSource(currentFilters);
        this.refreshActivityCounts();
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.GHGVerification), this.refreshGhgStatus);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.GHGVerification), this.refreshGhgStatus);
        super.detached();
    }

    refreshGhgStatus = async (param: EntityStatusChangeModel) => {
        if (this._isReloading) {
            console.log('refreshGhgStatus is already in progress, skipping...');
            return;
        }

        try {
            if (this.dataGridVM) {
                this._isReloading = true;
                if (this.dataGridVM.page == 0) {
                    await this.dataGridVM.reloadData();
                } else {
                    const item = this.dataGridVM.itemsCollection.filter(x => x.item.ghgVerificationId == param.entityId)[0];
                    if (item) {
                        item.item.statusId = param.statusId;
                        item.item.status = this.ghgStatuses.filter(x => x.id == param.statusId).map(x => x.name)[0];
                    }
                }
                await this.refreshActivityCounts();
            }
        }
        finally {
            this._isReloading = false;
        }
    }


    public get isSubFilterOpen(): boolean {
        return this.showBUFilter || this.showCustomerFilter || this.showGhgOutcomeFilter || this.showTaskTypeFilter || this.showColumnSorting
            || this.showDocCompanyFilter || this.showDwtFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showColumnSorting = false;
        this.showBUFilter = false;
        this.showCustomerFilter = false;
        this.showGhgOutcomeFilter = false;
        this.showTaskTypeFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public mobileFilterClick(value: LookupValue) {
        const index = this.mobileSelectedFilter.indexOf(value.id);
        const isSelected = index != -1;

        if (!isSelected) {
            this.mobileSelectedFilter.push(value.id);
        } else this.mobileSelectedFilter.splice(index, 1);
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        this.selectedStatusFilter = [];
        const column = this.columns.filter(x => x.name == 'status')[0];
        column.filters = [];

        if (this.mobileSelectedFilter && this.mobileSelectedFilter.length > 0) {
            for (const item of this.mobileSelectedFilter)
                this.filterClick({ id: item, name: '' });
        } else {
            this.setItemSource();
        }
    }

    clearAllFilters(reload: boolean = true) {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
            this.isColumnFiltering = false;
        }
        this.appliedFilter = null;
        this.filter = null;
        this.selectedBUs = [];
        this.selectedCustomers = [];
        this.selectedGhgOutcomes = [];
        this.selectedTaskTypes = [];
        this.mobileSelectedFilter = [];
        this.selectedStatusFilter = [];
        this.selectedColumnSorting = [];
        this.myGhgsFilterSelected = false;
        this.rightshipMember = false;
        this.isPendingVet = false;
        this.selectedDocCompanies = [];
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.isPendingDueDiligence = false;
        if (reload)
            this.setItemSource();
    }

    public get mobileFilterSelected() {
        return this.showCustomerFilter || this.showGhgOutcomeFilter || this.showBUFilter || this.showColumnSorting || this.showTaskTypeFilter
            || this.showDocCompanyFilter || this.showDwtFilter;
    }

    public closeAllFilters() {
        this.showColumnSorting = false;
        this.showBUFilter = false;
        this.showCustomerFilter = false;
        this.showGhgOutcomeFilter = false;
        this.showTaskTypeFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    //#region business unit filter

    //business filter
    public showBUFilter: boolean = false;
    public showCustomerFilter: boolean = false;
    public showGhgOutcomeFilter: boolean = false;
    public showTaskTypeFilter: boolean = false;
    @cachable
    public selectedBUs: number[] = [];
    @cachable
    public selectedCustomers: number[] = [];
    @cachable
    public selectedGhgOutcomes: number[] = [];
    @cachable
    public selectedTaskTypes: number[] = [];

    public toggleBUFilters() {
        if (!this.showBUFilter) {
            this.closeAllFilters();
        }
        this.showBUFilter = !this.showBUFilter;
        this.selectedFilter = {
            title: "Business Unit",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyBUFilter(this.selectedBUs.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public toggleCustomerFilters() {
        if (!this.showCustomerFilter) {
            this.closeAllFilters();
        }
        this.showCustomerFilter = !this.showCustomerFilter;
        this.selectedFilter = {
            title: "Customer",
            clear: () => {
                this.clearCustomerFilter();
            },
            apply: () => {
                this.applyCustomerFilter(this.selectedCustomers.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public toggleGhgOutcomeFilters() {
        if (!this.showGhgOutcomeFilter) {
            this.closeAllFilters();
        }
        this.showGhgOutcomeFilter = !this.showGhgOutcomeFilter;
        this.selectedFilter = {
            title: "GHG Outcome",
            clear: () => {
                this.clearGhgOutcomeFilter();
            },
            apply: () => {
                this.applyGhgOutcomeFilter(this.selectedGhgOutcomes.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public toggleTaskTypeFilters() {
        if (!this.showTaskTypeFilter) {
            this.closeAllFilters();
        }
        this.showTaskTypeFilter = !this.showTaskTypeFilter;
        this.selectedFilter = {
            title: "GHG Information",
            clear: () => {
                this.clearTaskTypeFilter();
            },
            apply: () => {
                this.applyTaskTypeFilter(this.selectedTaskTypes.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyBUFilter(items: any[], reload: boolean = true) {
        this.selectedBUs = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleBUFilters();
    }

    public applyCustomerFilter(items: any[], reload: boolean = true) {
        this.selectedCustomers = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleCustomerFilters();
    }

    public applyGhgOutcomeFilter(items: any[], reload: boolean = true) {
        this.selectedGhgOutcomes = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleGhgOutcomeFilters();
    }

    public applyTaskTypeFilter(items: any[], reload: boolean = true) {
        this.selectedTaskTypes = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleTaskTypeFilters();
    }

    public clearBUFilter() {
        this.selectedBUs = [];
        this.setItemSource();
    }

    public clearCustomerFilter() {
        this.selectedCustomers = [];
        this.setItemSource();
    }

    public clearGhgOutcomeFilter() {
        this.selectedGhgOutcomes = [];
        this.setItemSource();
    }

    public clearTaskTypeFilter() {
        this.selectedTaskTypes = [];
        this.setItemSource();
    }

    @computedFrom("selectedBUs", "selectedBUs.length")
    public get buFilterActive() {
        return this.selectedBUs && this.selectedBUs.length > 0;
    }

    @computedFrom("selectedCustomers", "selectedCustomers.length")
    public get customerFilterActive() {
        return this.selectedCustomers && this.selectedCustomers.length > 0;
    }

    @computedFrom("selectedGhgOutcomes", "selectedGhgOutcomes.length")
    public get ghgOutcomeFilterActive() {
        return this.selectedGhgOutcomes && this.selectedGhgOutcomes.length > 0;
    }

    @computedFrom("selectedTaskTypes", "selectedTaskTypes.length")
    public get taskTypeFilterActive() {
        return this.selectedTaskTypes && this.selectedTaskTypes.length > 0;
    }

    //#endregion

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    @computedFrom("userClientService.currentUser.id")
    get buParams() {
        const canViewAll = this.userClaimsService.hasPermission(Claims.RightShipUser);
        const customerId = this.userClientService.currentUser.settings.customerId ? this.userClientService.currentUser.settings.customerId : 'null';

        return customerId + ',' + (canViewAll ? 'null' : this.userClientService.currentUser.id);
    }

    //#region column sorting 

    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
        this.isColumnFiltering = this.dataGridVM.visibleColumns.some(item => item.isAscending === true || item.isAscending === false);
        this.setItemSource();
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion

    //#region doc company filter
    public showDocCompanyFilter: boolean = false;
    @cachable
    public selectedDocCompanies: number[] = [];

    public toggleDocCompanyFilters() {
        if (!this.showDocCompanyFilter) {
            this.closeAllFilters();
        }
        this.showDocCompanyFilter = !this.showDocCompanyFilter;
    }

    public applyDocCompanyFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedDocCompanies = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleDocCompanyFilters();
    }

    public clearDocCompanyFilter() {
        this.selectedDocCompanies = [];
        this.setItemSource();
    }

    @computedFrom("selectedDocCompanies", "selectedDocCompanies.length")
    public get docCompanyFilterActive() {
        return this.selectedDocCompanies?.length > 0 && this.selectedDocCompanies[0] != null;
    }

    //#endregion

    //#region dwt filter
    @cachable
    public dwtFirstFilter: number;
    @cachable
    public dwtSecondFilter: number;
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.setItemSource();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region due diligence filter
    
    public pendingDueDiligenceCount: number = 0;
    public canAccessDueDiligence: boolean;
    @cachable
    public isPendingDueDiligence: boolean;
    
    @computedFrom(Reflection.getPropertyPath<GhgVerificationActivities>(z => z.canAccessDueDiligence),
        Reflection.getPropertyPath<GhgVerificationActivities>(z => z.rightShipUser))
    public get dueDiligenceFilterVisible(): boolean
    {
        return this.canAccessDueDiligence && this.rightShipUser;
    }

    pendingDueDiligenceClick(isMobile: boolean = false) {
        this.isPendingDueDiligence = !this.isPendingDueDiligence;
        if (!isMobile) this.setItemSource();
    }

    //#endregion

    getTaskTypeFilters(): QueryFilter[] {
        const selectedTypes = this.taskTypes.filter(x => this.selectedTaskTypes.indexOf(x.id) > -1);
        const guid = Guid.generateGuid();
        return selectedTypes.map(x => ({
            filterOperator: {
                friendlyName: "eq",
                requiresFilterValue: true,
                requiresInterpolation: false,
                translatedSymbol: "eq"
            },
            filterProperty: {
                name: x.propertyName,
                isDynamicProperty: false,
                type: DataType.Boolean,
                isNullable: true
            },
            filterValue: true,
            isAnd: true,
            skipOnNullOrEmpty: true,
            groupGuid: guid
        }));
    }
}
